import { Box, Grid, Theme, Typography, useMediaQuery } from '@material-ui/core';
import React, { FC, useEffect } from 'react';
import { makeStyles } from '@material-ui/styles';
import { BusinessDetails, LightingAudit, Retrofits, Upgrade } from 'assets/svgs/led';
import ProjectSection from './ProjectSection';
import ProjectCard from './ProjectCard';
import ledStores from 'stores/led';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import { triggerLedProjectBeginCreation, triggerLedProjectContinue } from 'config/seo/events';
import useI18n from "evcharging/hooks/useI18n";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(19, 5, 0),
    [theme.breakpoints.up('lg')]: {
      overflowY: 'scroll',
      height: '100vh',
    },
  },
  secondBlock: {
    backgroundColor: theme.palette.common.white,
    width: '100%',
    borderRadius: theme.spacing(3),
    marginBottom: theme.spacing(5),
    padding: theme.spacing(0, 4),
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(0, 10),
    },
  },
  textHowItWorks: {
    maxWidth: 780,
  },
  thirdBlock: {
    backgroundColor: theme.palette.common.white,
    width: '100%',
    borderRadius: theme.spacing(3),
    marginBottom: theme.spacing(12),
    marginTop: theme.spacing(10),
  },
}));

const ContentSection: FC = observer(() => {
  const { startPointStore, ledLayoutStore } = ledStores;
  const { t } = useI18n()
  const router = useRouter();
  const { isLoading, openProjectIntroConfirmation, alreadyHasProject, initialize, isPageLoading } =
    startPointStore;

  const classes = useStyles();
  const isSmall = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const listItems = [
    {
      title: t('intro.list.item1'),
      text: t('intro.list.item1-text'),
      svgIcon: <BusinessDetails />,
    },
    {
      title: t('intro.list.item2'),
      text: t('intro.list.item2-text'),
      svgIcon: <LightingAudit />,
    },
    {
      title: t('intro.list.item3'),
      text: t('intro.list.item3-text'),
      svgIcon: <Retrofits />,
    },
    {
      title: t('intro.list.item4'),
      text: t('intro.list.item4-text'),
      svgIcon: <Upgrade />,
    },
  ];

  const onClickContinue = async () => {
    await router.push(ledLayoutStore.lastUserStep);
    triggerLedProjectContinue();
  };

  const onClickStartProject = async () => {
    openProjectIntroConfirmation();
    triggerLedProjectBeginCreation();
  };

  useEffect(() => {
    initialize();
  }, []);

  if (isPageLoading) {
    return null;
  }

  return (
    <Grid container className={classes.root} justifyContent="center">
      <Grid item xs={12}>
        {alreadyHasProject() ? (
          <ProjectSection
            title={t('intro.already-have')}
            description={t('intro.you-can-continue')}
            button={t('intro.continue')}
            secondButton={t('intro.start')}
            onClick={onClickContinue}
            loading={isLoading}
            onContinue={onClickStartProject}
          />
        ) : (
          <ProjectSection
            title={t('intro.start-now')}
            description={t('intro.get-upgrade')}
            button={t('intro.start')}
            onClick={openProjectIntroConfirmation}
            loading={isLoading}
          />
        )}
      </Grid>
      <Grid item xs={12}>
        <Box className={classes.secondBlock}>
          <Box mb={2} pt={8}>
            <Typography variant="h3" align={isSmall ? 'center' : 'left'}>
              {t('intro.how-it-works')}
            </Typography>
          </Box>
          <Box pb={8}>
            <Typography
              variant="body2"
              className={classes.textHowItWorks}
              align={isSmall ? 'center' : 'left'}
            >
              {t('intro.how-it-works.description')}
            </Typography>
          </Box>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Box mb={14}>
          <ProjectCard list={listItems} />
        </Box>
      </Grid>
    </Grid>
  );
});

export default ContentSection;
